import React, { useCallback, useState } from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import TextField from '@material-ui/core/TextField';
import { ResponseErrorPanel } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import {
  catalogImportApiRef,
  StepInitAnalyzeUrlProps,
} from '@backstage/plugin-catalog-import';
import { errorHeadline } from '../../lib/errors';

/**
 * Replaces the analyze step of the catalog import stepper. Behaves like the
 * upstream step, but renders failures in an expandable error panel instead of
 * a single line of helper text.
 */
export const StepAnalyzeUrl = (props: StepInitAnalyzeUrlProps) => {
  const {
    onAnalysis,
    analysisUrl = '',
    disablePullRequest = false,
    exampleLocationUrl = 'https://git.cnvrmedia.net/projects/GPSA/repos/my-repo/browse/catalog-info.yaml',
  } = props;

  const catalogImportApi = useApi(catalogImportApiRef);
  const [url, setUrl] = useState(analysisUrl);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<Error | undefined>();

  const isValidUrl = /^https?:\/\//.test(url);

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setSubmitted(true);
      setError(undefined);

      const fail = (reason: Error) => {
        setError(reason);
        setSubmitted(false);
      };

      try {
        const result = await catalogImportApi.analyzeUrl(url);

        if (result.type === 'repository') {
          if (disablePullRequest || result.generatedEntities.length === 0) {
            fail(new Error("Couldn't generate entities for your repository"));
            return;
          }
          onAnalysis('no-location', url, result);
        } else if (result.locations.length === 1) {
          onAnalysis('single-location', url, result, { prepareResult: result });
        } else if (result.locations.length > 1) {
          onAnalysis('multiple-locations', url, result);
        } else {
          fail(new Error('There are no entities at this location'));
        }
      } catch (e) {
        fail(e instanceof Error ? e : new Error(String(e)));
      }
    },
    [catalogImportApi, disablePullRequest, onAnalysis, url],
  );

  return (
    <form onSubmit={handleSubmit}>
      <TextField
        fullWidth
        id="url"
        label="URL"
        value={url}
        onChange={event => {
          setUrl(event.target.value);
          setError(undefined);
        }}
        placeholder={exampleLocationUrl}
        helperText={
          url && !isValidUrl
            ? 'Must start with http:// or https://.'
            : 'Enter the full path to your entity file to start tracking your component'
        }
        error={Boolean(url) && !isValidUrl}
        margin="normal"
        variant="outlined"
        required
      />

      {error && (
        <Box mt={1} mb={2}>
          <ResponseErrorPanel title={errorHeadline(error)} error={error} />
        </Box>
      )}

      <Box mt={1} position="relative" display="inline-block">
        <Button
          color="primary"
          variant="contained"
          type="submit"
          disabled={!isValidUrl || submitted}
        >
          Analyze
        </Button>
        {submitted && (
          <CircularProgress
            size="1.5rem"
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              marginTop: -12,
              marginLeft: -12,
            }}
          />
        )}
      </Box>
    </form>
  );
};
